import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  useDeletePickupLocation,
  usePickupLocations,
  useSetDefaultPickupLocation,
} from '@/api/domains/pickup-locations';
import type { PickupLocation } from '@/api/types/pickups';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { PickupLocationFormDialog } from '../components/PickupLocationFormDialog';

type Props = {
  dirtyRef: (dirty: boolean) => void;
};

export function PickupLocationsTab({ dirtyRef }: Props) {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  const locations = usePickupLocations();
  const remove = useDeletePickupLocation();
  const setDefault = useSetDefaultPickupLocation();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PickupLocation | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PickupLocation | null>(null);

  // These tabs persist via dialogs, so there is never unsaved page-level state.
  useEffect(() => dirtyRef(false), [dirtyRef]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (location: PickupLocation) => {
    setEditing(location);
    setFormOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(t('pickupLocations.deleted'));
        setDeleteTarget(null);
      },
      onError: (error) => toastProblem(toApiProblem(error)),
    });
  };

  const rows = locations.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-medium tracking-[-0.6px] text-black">
            {t('pickupLocations.title')}
          </h3>
          <p className="text-base text-muted-foreground">{t('pickupLocations.description')}</p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="size-4" />
          {t('pickupLocations.add')}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#e5e5e5]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('pickupLocations.fields.name')}</TableHead>
              <TableHead>{t('pickupLocations.address')}</TableHead>
              <TableHead className="w-44 text-right">{tCommon('actions.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {locations.isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                  {tCommon('states.loading')}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                  {t('pickupLocations.empty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((location) => (
                <TableRow key={location.id}>
                  <TableCell className="font-medium text-black">
                    <div className="flex items-center gap-2">
                      {location.name}
                      {location.is_default ? (
                        <Badge variant="secondary">{t('pickupLocations.default')}</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {`${location.street} ${location.house_number}, ${location.postal_code} ${location.city}`}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {!location.is_default ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title={t('pickupLocations.setDefault')}
                          onClick={() => setDefault.mutate(location.id)}
                        >
                          <Star className="size-4" />
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={tCommon('actions.edit')}
                        onClick={() => openEdit(location)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={tCommon('actions.delete')}
                        onClick={() => setDeleteTarget(location)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PickupLocationFormDialog open={formOpen} onOpenChange={setFormOpen} location={editing} />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pickupLocations.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pickupLocations.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={remove.isPending}>
              {tCommon('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
